/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import AuthService from '../../service/auth';
import AmplitureService from '../../service/amplitude';
import { uuid } from 'vue-uuid';
import WyFilter from '../../library/wy-filter';
import WyUtils from '../../library/wy-utils.js';
import WyPendo from '../../library/wy-pendo.js';
import WyConstants from '../../library/wy-constants';
import moment from 'moment'
import _ from 'lodash';
import deepmerge from 'deepmerge';
import { jwtDecode } from "jwt-decode";

const state = {
  config: {},
  authUser: {},
  globalConfig: {},
  companyConfig: {},
  defaultRoutePath: null,
  companyProfileSettings: {},
  companyProfilePrompts: {},
  trackInfo: {
    loadedTimeLong: 0
  },
  tokenInfo: {}
}

const getters = {
  config: state => state.config,
  
  isLoggedIn: state => {
    if(!state.authUser.token && window.sessionStorage.getItem(WyConstants.SESSION_AUTH_USER)) {
      try {
        state.authUser = JSON.parse(window.sessionStorage.getItem(WyConstants.SESSION_AUTH_USER));
        state.authUser.loginDate = new Date(state.authUser.loginDate);
      }
      catch(e) {
        state.authUser = {};
        console.error(e);
      }
    }

    if((!state.tokenInfo || Object.keys(state.tokenInfo) == 0) && window.sessionStorage.getItem(WyConstants.SESSION_AUTH_USER + '-tokenInfo')) {
      try {
        if(window.sessionStorage.getItem(WyConstants.SESSION_AUTH_USER + '-tokenInfo')) {
          state.tokenInfo = JSON.parse(window.sessionStorage.getItem(WyConstants.SESSION_AUTH_USER + '-tokenInfo'));
        }
      }
      catch(e) {
        state.tokenInfo = {};
        console.error(e);
      }
    }

    return (state.authUser && state.authUser.token);
  },

  authUser: state => {
    if(!state.authUser.token && window.sessionStorage.getItem(WyConstants.SESSION_AUTH_USER)) {
      try {
        state.authUser = JSON.parse(window.sessionStorage.getItem(WyConstants.SESSION_AUTH_USER));
        state.authUser.loginDate = new Date(state.authUser.loginDate);
      }
      catch(e) {
        state.authUser = {};
        console.error(e);
      }
    }

    return state.authUser;
  },

  tokenInfo: state => {
    return state.tokenInfo;
  },

  getBrowserSessionStorageByUser: state => {
    return function (key) {
      return WyUtils.getBrowserSessionStorageByUser(state.authUser, key);
    }
  },

  getBrowserLocalStorageByUser: state => {
    return function (key) {
      return WyUtils.getBrowserLocalStorageByUser(state.authUser, key);
    }
  },

  accessHost: state => {
    return function (userType) {
      const locationOrigin = window.location.origin;
      const locationOriginHost = locationOrigin.replace('https://', '').replace('http://', '');
      const host = locationOriginHost.replace(/\./g, '_');

      let finalHost = locationOrigin;      
      if(userType == 'CLOUD' && state.globalConfig && state.globalConfig.cloudURLMap && state.globalConfig.cloudURLMap[host]) {
        const cloudHost =  state.globalConfig.cloudURLMap[host];
        finalHost = (locationOrigin+"").replace(locationOriginHost, cloudHost);
      }

      return finalHost;
    }
  },

  globalConfig: state => {
    return state.globalConfig;
  },

  companyConfig: state => {
    return state.companyConfig;
  },

  companyProfileSettings: state => {
    return state.companyProfileSettings;
  },

  companyProfilePrompts: state => {
    return state.companyProfilePrompts;
  },

  isC1Auth: () => {
    return WyConstants.C1_DEV_MODE || window.parent.frames.length > 0;
  },

  isC1BotAnaltyics: (state, rootState) => {
    const productName = rootState.config.appID;
    return ((WyConstants.C1_DEV_MODE || window.parent.frames.length > 0) && productName && productName == WyConstants.PRODUCT_NAME_BA);
  },

  isC1Interactions: (state, rootState) => {
    const productName = rootState.config.appID;
    return ((WyConstants.C1_DEV_MODE || window.parent.frames.length > 0) && productName && productName == WyConstants.PRODUCT_NAME_IA);
  },

  isC1AutoQA: (state, rootState) => {
    const productName = rootState.config.appID;
    return ((WyConstants.C1_DEV_MODE || window.parent.frames.length > 0) && productName && productName == WyConstants.PRODUCT_NAME_AQ);
  },

  isC1AutoAS: (state, rootState) => {
    const productName = rootState.config.appID;
    return ((WyConstants.C1_DEV_MODE || window.parent.frames.length > 0) && productName && productName == WyConstants.PRODUCT_NAME_AS);
  },

  isC1SmartTags: (state, rootState) => {
    const productName = rootState.config.appID;
    return ((WyConstants.C1_DEV_MODE || window.parent.frames.length > 0) && productName && productName == WyConstants.PRODUCT_NAME_ST);
  },

  branding: (state, rootState) => {
    return rootState.config.appID;
  },
  
  defaultRoutePath: state => {
    if(window.sessionStorage.getItem(WyConstants.SESSION_CURRENT_ROUTE_PATH) && !state.defaultRoutePath) {
      try {
        state.defaultRoutePath = window.sessionStorage.getItem(WyConstants.SESSION_CURRENT_ROUTE_PATH);
      }
      catch(e) {
        state.defaultRoutePath = null;
        console.error(e);
      }
    }

    return state.defaultRoutePath;
  },

  xnumber: state => {
    return (state.companyConfig && state.companyConfig.xnumber > 0) ? state.companyConfig.xnumber : 1;
  },

  isAdminUser: state => {
    return (state.authUser.user && (state.authUser.user.role == "SYSTEM_ADMIN" || state.authUser.user.role == "ACCOUNT_ADMIN"));
  },

  isSysAdminUser: state => {
    return (state.authUser.user && state.authUser.user.role == "SYSTEM_ADMIN");
  },

  isReadOnlyUser: state => {
    return (state.authUser.user && state.authUser.user.role == "USER_READ_ONLY");
  },

  isProductAllow: () => {
    return function (menuItem) {
      const allCodes = []
      if(menuItem.aclCodes) allCodes.push(...menuItem.aclCodes);
      if(menuItem.subItems) {
        menuItem.subItems.forEach(subItem => {
          if(subItem.aclCodes) allCodes.push(...subItem.aclCodes);
        })      
      }
      return (this.isRoleAllow(allCodes) && !menuItem.isHidden);
    }
  },

  isRoleAllow: state => {
    return function (code, isAllow = false) {
      if(getters.isSysAdminUser(state)) {
        isAllow = true;
      }
      else if(state.authUser.company && code && Array.isArray(code)) {
        isAllow = (code.find(permission => state.authUser.company.permissions.includes(permission)) != null ? true : false);
      }
      else if(state.authUser.company && code) {
        isAllow = state.authUser.company.permissions.includes(code);
      }
      return isAllow;
    }
  },

  isSpecialUser: state => {
    return state.authUser.user.email.includes('paul.vuong@') || state.authUser.user.email.includes('natalie.povzner@')
  },

  isHideResponse: state => {
    if(state.config.table && state.config.table.hideColumns && Array.isArray(state.config.table.hideColumns)) {
      return state.config.table.hideColumns.includes("Response Time")
    }
    return false;
  },

  appRoles: state => {
    let appRoles = state.config.appRoles;
    let roles = [state.authUser.company.isSystemCompany ? appRoles["SYSTEM_ADMIN"] : appRoles["ACCOUNT_ADMIN"]];
    roles.push(appRoles["USER"]);
    roles.push(appRoles["USER_READ_ONLY"]);
    return roles;
  },
}

const mutations = {
  updateAuthUser(state, options) {
    let authUser = {};
    authUser.uuid = uuid.v4();
    authUser.token = options.token;
    authUser.accessToken = options.accessToken || options.token;
    authUser.authHost = options.authHost;
    authUser.sessionId = options.sessionId;
    authUser.productName = options.productName;
    authUser.authOptions = options.authOptions;
    authUser.loginDate = new Date();    
    authUser.user = options.user;
    authUser.companyId = options.companyId;
    authUser.company = options.company;
    authUser.companyList = options.companyList;
    authUser.config = state.config;

    state.authUser = authUser
    state.authUrl = options.authUrl
    window.sessionStorage.setItem(WyConstants.SESSION_AUTH_USER, JSON.stringify(state.authUser));
  },

  updateAuthUserToken(state, token) {
    if(state.authUser) {
      state.authUser.token = token;
      state.authUser.accessToken = token;
      window.sessionStorage.setItem(WyConstants.SESSION_AUTH_USER, JSON.stringify(state.authUser));
    }
  },

  updateGlobalConfig(state, {data}) {
    state.globalConfig = data;
  },

  updateCompanyConfig(state, {data}) {
    state.companyConfig = data;
  },

  updateCompanyProfileSettings(state, data) {
    state.companyProfileSettings = data;
  },

  updateCompanyPromptSettings(state, data) {
    state.companyProfilePrompts = data
  },

  mergeAppConfigs(state, {data}) {
    // merge app panel configs
    if(data && data.appPanels && Array.isArray(data.appPanels)) {
      for(const index in state.config.appPanels) {
        let appConfigPanel = state.config.appPanels[index];
        let companyConfigPanel = data.appPanels.find(el => el.key == appConfigPanel.key);
        if(companyConfigPanel) {
          state.config.appPanels[index] = _.merge(appConfigPanel, companyConfigPanel);
        }
      }
    }

    // merge api configs from default/global/company configs
    if(data && data.api && data.api.configs) {
      // state.config.api.configs = _.merge(state.config.api.configs, data.api.configs);
      if(state.config && state.config.api) {
        // eslint-disable-next-line no-unused-vars
        state.config.api.configs = deepmerge(state.config.api.configs, data.api.configs, { arrayMerge: (destinationArray, sourceArray, options) => sourceArray });
      }
    }

    // merge app configs from default/global/company configs
    if(data && data.app && data.app.configs) {
      if(state.config.app && state.config.app.configs) {
        state.config.app.configs = _.merge(state.config.app.configs, data.app.configs);
      }
    }

    // merge tabs from default/global/company configs
    if(data && data.tabs) {
      state.config.tabs = _.merge(state.config.tabs, data.tabs);
    }

    state.authUser.config = state.config;
    window.sessionStorage.setItem(WyConstants.SESSION_AUTH_USER, JSON.stringify(state.authUser));
  },

  updateSelectedCompany(state, {token, companyId}) {
    state.authUser.accessToken = token;
    state.authUser.config = state.config;
    // state.authUser.isDevelopment = state.globalConfig.isDevelopment;
    // state.authUser.amplitudeDebug = state.globalConfig.amplitudeDebug;

    if(companyId && state.authUser.companyList) {
      // set selected product
      let company = state.authUser.companyList.find(el => el.id == companyId);
      company.isSystemCompany = (company.companyCode == WyConstants.systemCompanyCode);

      state.authUser.companyId = companyId;
      state.authUser.company = company;
      // set company role
      if(state.authUser.user.roles && Array.isArray(state.authUser.user.roles)) {
        state.authUser.role = state.authUser.user.roles.find(el => el.companyId == companyId);
      }
      window.sessionStorage.setItem(WyConstants.SESSION_AUTH_USER, JSON.stringify(state.authUser));      
    }
  },

  updateTokenInfo(state, tokenObj) {
    try {
      state.tokenInfo = jwtDecode(tokenObj);
      window.sessionStorage.setItem(WyConstants.SESSION_AUTH_USER + '-tokenInfo', JSON.stringify(state.tokenInfo));
    } 
    catch(e) {
      console.error(e);
    }
  },

  updateDefaultRoutePath(state, path) {
    if(path != "/nodata") {
      state.defaultRoutePath = path
      window.sessionStorage.setItem(WyConstants.SESSION_CURRENT_ROUTE_PATH, state.defaultRoutePath);
    }    
  },

  updateConfig(state, data) {
    if(data && data.navMenu) {
      data.navMenu.items.forEach(item => {
        item.isActive = false;
        if(item.acl) item.aclCodes = Object.keys(item.acl).map(val => parseInt(val));
        if(item.subItems) {
          item.subItems.forEach(subItem => {
            subItem.isActive = false;
            if(subItem.acl) subItem.aclCodes = Object.keys(subItem.acl).map(val => parseInt(val));
          })
        }
      });
    }
    state.config = data;
  },

  resetLogin(state) {
    state.authUser = {};
  },
}

const actions = {
  logAmplitudeWithFilters({getters, state}, {eventName, eventProperties, filterProperties}) {
    let ampPayload = eventProperties || {};

    // don't send filter properties if it is set to false
    if(filterProperties !== false) {
      // global filter
      ampPayload.filter_settings = Object.assign(WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A), filterProperties || {});
      if(getters.dataModel.dateStartCompare) ampPayload.filter_settings.dateStartCompare = moment(getters.dataModel.dateStartCompare).format("YYYY-MM-DD");
      if(getters.dataModel.dateEndCompare) ampPayload.filter_settings.dateEndCompare = moment(getters.dataModel.dateEndCompare).format("YYYY-MM-DD");

      // date range selection
      if(getters.dataModel.dateRangeType == -1) {
        ampPayload.filter_settings.dateRangeType = getters.dataModel.dateRangeOptions[getters.dataModel.dateRangeOptions.length-1].label;
      }
      else if(getters.dataModel.dateRangeType > -1 && getters.dataModel.dateRangeType-1 < getters.dataModel.dateRangeOptions.length) {
        ampPayload.filter_settings.dateRangeType = getters.dataModel.dateRangeOptions[getters.dataModel.dateRangeType-1].label;
      }
    }

    if(ampPayload.where) state.trackInfo.where = ampPayload.where;

    // log time spent
    const currTimeLong = (new Date()).getTime();
    if(eventName == "page_time_spent") {
      eventProperties.time_spent = currTimeLong - state.trackInfo.loadedTimeLong;
      state.trackInfo.loadedTimeLong = currTimeLong;
    }
    else if(state.trackInfo.loadedTimeLong == 0) {
      state.trackInfo.loadedTimeLong = currTimeLong;
    }

    AmplitureService.eventLog(eventName, getters.authUser, ampPayload);
  },

  amplitudeLog({getters}, {name, data}) {
    // date range selection
    if (data && data.filter_settings && getters.dataModel.dateRangeOptions && getters.dataModel.dateRangeOptions.length > 0) {
      if(getters.dataModel.dateRangeType == -1) {
        data.filter_settings.dateRangeType = getters.dataModel.dateRangeOptions[getters.dataModel.dateRangeOptions.length-1].label;
      }
      else if(getters.dataModel.dateRangeType > -1 && getters.dataModel.dateRangeType-1 < getters.dataModel.dateRangeOptions.length) {
        data.filter_settings.dateRangeType = getters.dataModel.dateRangeOptions[getters.dataModel.dateRangeType-1].label;
      }
    }

    AmplitureService.eventLog(name, getters.authUser, data);
  },

  amplitudeLogWithFilters({getters}, {name, dateMode, filter, data, dateRanges}) {
    let ampPayload = {}

    if(dateRanges) {
      ampPayload.filter_settings = Object.assign(WyFilter.filterValues(getters.shareState, getters.dataModel), dateRanges)
    }
    else {
      ampPayload.filter_settings = WyFilter.filterValues(getters.shareState, getters.dataModel, (dateMode ? dateMode : WyFilter.MODE_DATE_A))
    }

    if(filter) {
      ampPayload.filter_settings = Object.assign(ampPayload.filter_settings, filter);
    }

    // date range selection
    if (ampPayload && ampPayload.filter_settings && getters.dataModel.dateRangeOptions && getters.dataModel.dateRangeOptions.length > 0) {
      if(getters.dataModel.dateRangeType == -1) {
        ampPayload.filter_settings.dateRangeType = getters.dataModel.dateRangeOptions[getters.dataModel.dateRangeOptions.length-1].label;
      }
      else if(getters.dataModel.dateRangeType > -1 && getters.dataModel.dateRangeType-1 < getters.dataModel.dateRangeOptions.length) {
        ampPayload.filter_settings.dateRangeType = getters.dataModel.dateRangeOptions[getters.dataModel.dateRangeType-1].label;
      }
    }
    
    if(data) {
      ampPayload = Object.assign(ampPayload, data);
    }
    AmplitureService.eventLog(name, getters.authUser, ampPayload);
  },

  amplitudeLogFetchData({getters, dispatch}, {eventName, payload, options}) {
    let today = new Date();
    
    // ampliture payload
    let ampPayload = JSON.parse(JSON.stringify(payload));
    ampPayload.dateStartCompare = moment(getters.dataModel.dateStartCompare).format("YYYY-MM-DD");
    ampPayload.dateEndCompare = moment(getters.dataModel.dateEndCompare).format("YYYY-MM-DD");

    // ampliture loggingg
    if(getters.moreFiltersChanged > 0) {
      dispatch('amplitudeLog', {name: `${eventName}_with_more_filter_applied`, data: {filter_settings: ampPayload}});
    }
    else if(getters.dataModel.dateHasChanged) {
      getters.dataModel.dateHasChanged = false;
      dispatch('amplitudeLog', {name: `${eventName}_with_date_filter_changed`, data: {filter_settings: ampPayload}});
    }
    else if(getters.dataModel.filters.custTypes.hasChanged) {
      getters.dataModel.filters.custTypes.hasChanged = false;
      dispatch('amplitudeLog', {name: `${eventName}_with_channel_filter_changed`, data: {filter_settings: ampPayload}});
    }
    else if(!options || !options.forceRefresh || options.logEvent) {
      dispatch('amplitudeLog', {name: eventName, data: {filter_settings: ampPayload, data_load_time: WyUtils.getTimeDiff(today)}});
    }
  },

  pendoInit({getters}) {
    const user = getters.authUser.user;
    if(getters.isC1Auth && user && user.company) {
      const pendoApiKey = 'd8784f17-4f82-44f7-4fa4-9551db776393';
      // inject pendo script to the current browser page
      const pendoInstance = WyPendo.createPendoInstance(pendoApiKey);
      if(pendoInstance) {
        // initialize pendo with logged in user info
        const pendoUserId = user.company.name + '-' + user.id;
        const userHasScope = user.teamsInScope ? user.teamsInScope.length > 0 : false;

        pendoInstance.initialize({
          disableGuides: true,
          visitor: {
            id: pendoUserId,
            personId: user.id,
            tenantId: user.companyId,
            tenantName: user.company.name,
            team: user.teamName,
            c1Permissions: user.c1Permissions,
            wfmPermissions: [], // TODO: will be part of IAM service
            localization: user.lang,
            timezone: user.timezoneString,
            c1Scoping: userHasScope,
          },
          account: {
            id: user.pendoId, // tenantName-tenantId
          },
        });
      }
    }
  },

  async login({dispatch}, form) {
    return new Promise(async (resolve) => {        
      let res = await AuthService.authToken(form);
      if(res && res.status == 200) {
        if(res.data.errorCode == 0 && res.data.token) {
          let isOK = await dispatch('fetchProfile', res.data.token);
          if(!isOK) {
            res.status = 401;
            res.data.errorCode = 1003;
          }
        }
      }
      else if(res && res.status == 401 && res.data && res.data.errorCode == 5002) {
        await dispatch('logoutAndReload');
      }
      resolve(res);
    });
  },

  async logoutAndReload({commit}, authUrl) {
    // reset user data
    commit('resetLogin');

    window.sessionStorage.clear();

    if(authUrl) {
      setTimeout(() => {
        window.location.href = `${authUrl}&ts=${(new Date).getTime()}`;
      }, 100);
    }
    else {
      // reset default path
      commit('updateDefaultRoutePath', null);
    
      setTimeout(() => {
        window.location = `${location.origin}${location.pathname}?ts=${(new Date).getTime()}`;
      }, 1200);
    }
  },

  async fetchProfile({state, commit, dispatch}, token) {
    // get user profile
    let resProfile = await AuthService.authProfile(token);
    if(resProfile.status == 200 && resProfile.data.user && resProfile.data.detailAccessList) {
      let defaultCompanyIdId = resProfile.data.user.defaultCompanyId;

      let companyList = [];
      let defaultCompany = null;
      resProfile.data.detailAccessList.forEach((detailAccess) => {
        // set permissions to company
        detailAccess.company.permissions = detailAccess.permissions || [];

        // set products to company
        if(resProfile.data.user.role=="SYSTEM_ADMIN") {
          detailAccess.company.products = detailAccess.products;
        }
        else {
          detailAccess.company.products = [];
          
          state.config.navMenu.items.filter(item => {          
            const menuItemAllCodes = [];
            if(item.aclCodes) {
              menuItemAllCodes.push(...item.aclCodes, item);
            }

            if(item.subItems) {
              item.subItems.map(subItem => {
                if(item.aclCodes) {
                  menuItemAllCodes.push(...subItem.aclCodes);
                }
              });
            }
            
            const hasAclCodes = _.intersection(detailAccess.company.permissions, menuItemAllCodes).length>0;
            if(hasAclCodes) {
              // add product code to this company if acl codes found
              detailAccess.company.products.push({
                name: item.label,
                productCode: item.productCode
              });
            }
          });
        }

        // set default product for company
        if(detailAccess.company.products.length > 0) {
          detailAccess.company.selectedProduct = detailAccess.company.products[0];
        }

        delete detailAccess.products;
        delete detailAccess.permissions;
        companyList.push(detailAccess.company);
        if(!defaultCompany) {
          if(defaultCompanyIdId && defaultCompanyIdId == detailAccess.company.id) {
            defaultCompany = detailAccess.company;
          }
        }
      });


      if(!defaultCompany && resProfile.data.detailAccessList.length > 0) {
        // set first company as default company
        defaultCompany = resProfile.data.detailAccessList[0].company;
      }

      // set access token
      if(defaultCompany) {
        let resToken = await AuthService.getAccessToken(defaultCompany.id, token);
        if(resToken.status == 200 && resToken.data.token) {
          commit('updateAuthUser', {token: token, accessToken: resToken.data.token, user: resProfile.data.user, companyList: companyList});

          await dispatch('fetchCompanyConfiguration', {accessToken: resToken.data.token, companyId: defaultCompany.id});
          //commit('updateSelectedCompany', {token: resToken.data.token, companyId: defaultCompany.id});

          return true;
        }
        else if(resToken && resToken.status == 401 && resToken.data && resToken.data.errorCode == 5002) {
          await dispatch('logoutAndReload');
        }
      }
    }

    return false;
  },

  async createPassword({dispatch}, form) {
    return new Promise(async (resolve) => {        
      let res = await AuthService.createPassword(form);
      if(res.status == 200) {
        if(res.data.errorCode == 0 && res.data.token) {
          let isOK = await dispatch('fetchProfile', res.data.token);
          if(!isOK) {
            res.status = 401;
            res.data.errorCode = 1003;
          }
        }
      }
      resolve(res);
    });
  },
  
  async refreshAccessToken({state, commit, dispatch}) {
    if(state.authUser.accessToken && state.authUser.accessToken != "") {
      let res = await AuthService.refreshAccessToken(state.authUser.accessToken);
      if(res.status == 200 && res.data.token) {
        commit('updateSelectedCompany', {token: res.data.token, companyId: state.authUser.companyId});
        return true;
      }
      else if(res && res.status == 401 && res.data && res.data.errorCode == 5002) {
        await dispatch('logoutAndReload');
        return false;
      }
    }
    return true;
  },

  async updateSelectedCompany({state, commit, dispatch}, {companyId}) {
    if(state.authUser.accessToken && state.authUser.accessToken != "") {
      let resToken = await AuthService.getAccessToken(companyId, state.authUser.accessToken);
      if(resToken.status == 200 && resToken.data.token) {
        commit('updateSelectedCompany', {token: resToken.data.token, companyId: companyId});
        return true;
      }
      else if(resToken && resToken.status == 401 && resToken.data && resToken.data.errorCode == 5002) {
        await dispatch('logoutAndReload');
      }
    }
    return false;
  },

  updateDefaultRoutePath({ commit, rootGetters }, path) {
    commit('updateDefaultRoutePath', path);

    const selectedProductMenu = rootGetters.config.navMenu.items.find(item => {
      return (item.path == path || (item.subItems && item.subItems.find(subItem => subItem.path == path)));
    });

    const productCode = selectedProductMenu ? selectedProductMenu.productCode : null;
    if(productCode) {
      if(rootGetters.companyConfig && rootGetters.companyConfig.api && rootGetters.companyConfig.api[productCode] && rootGetters.companyConfig.api[productCode].version) {
        rootGetters.shareState.apiVersion = rootGetters.companyConfig.api[productCode].version;
      }
      else if(rootGetters.globalConfig && rootGetters.globalConfig.api && rootGetters.globalConfig.api[productCode] && rootGetters.globalConfig.api[productCode].version) {
        rootGetters.shareState.apiVersion = rootGetters.globalConfig.api[productCode].version;
      }
    }
  },
  
  authRequestPassword(_, email) {
    let data = {
      email: email,
      fullUrl: window.location.origin + window.location.pathname + 'resetPassword'
    }
    return AuthService.authRequestPassword(data);
  },
  
  managementValidateRegistrationLink(_, data) {
    return AuthService.managementValidateRegistrationLink(data);
  },

  authRequestPasswordValidate(_, data) {
    return AuthService.authRequestPasswordValidate(data);
  },

  authResetPassword(_, data) {
    return AuthService.authResetPassword(data);
  },

  async authUpdatePassword({dispatch}, data) {
    let res = null;
    if(data.code == 1005) {
      res = await AuthService.authUpdateExpiredPassword(data)
    }
    else if(data.code == 2001) {
      res = await AuthService.authUpdateFirstTimePassword(data)
    }
    
    if(res && res.status == 200 && res.data.token) {
      let isOK = await dispatch('fetchProfile', res.data.token);
      if(!isOK) {
        res.status = 401;
        res.data.errorCode = 1003;
      }
    }
    return res;
  },

  accountChangePassword({getters}, data) {
    return AuthService.accountChangePassword(getters.authUser, data);
  },

  inviteUser({getters}, data) {
    return AuthService.inviteUser(getters.authUser, data);
  },

  resendInvitationEmail({getters}, data) {
    return AuthService.resendInvitationEmail(getters.authUser, data);
  },

  updateUserInfo({getters}, data) {
    return AuthService.updateUserInfo(getters.authUser, data.payload, data.userId);
  },

  getAccessibleCompanies({getters}) {
    return AuthService.getAccessibleCompanies(getters.authUser);
  },

  getPermissions({getters}) {
    return AuthService.getPermissions(getters.authUser);
  },

  getUsers({getters}, accountId) {
    return AuthService.getUsers(getters.authUser, accountId);
  },

  updateUserAccessList({getters}, data) {
    return AuthService.updateUserAccessList(getters.authUser, data.payload, data.userId);
  },

  updateUserRole({getters}, data) {
    return AuthService.updateUserRole(getters.authUser, data.payload, data.userId);
  },

  updateUserStatus({getters}, data) {
    return AuthService.updateUserStatus(getters.authUser, data.payload);
  },

  updateUserType({getters}, data) {
    return AuthService.updateUserType(getters.authUser, data.payload);
  },

  updateDefaultCompany({getters}, data) {
    return AuthService.updateDefaultCompany(getters.authUser, data.payload);
  },

  deleteUser({getters}, data) {
    return AuthService.deleteUser(getters.authUser, data.userId);
  },

  async fetchGlobalConfiguration({commit}) {
    try {
      let res = null;
      if(WyConstants.USE_OC_CONFIGURATION_API) {
        res = await AuthService.fetchGlobalConfigurations();
      }
      else {
        res = await AuthService.fetchGlobalConfiguration();
      }
      
      if(res && res.status == 200 && res.data) {
        commit('updateGlobalConfig', {data: res.data.config})
        commit('mergeAppConfigs', {data: res.data.config})
        return res.data.config;
      }
    }
    catch(e) {
      console.error("GlobalConfiguration not found", e)
    }
    return false;
  },

  async updateGlobalConfiguration({getters, commit}, data) {
    try {
      let res = null;
      if(WyConstants.USE_OC_CONFIGURATION_API) {
        res = await AuthService.updateGlobalConfigurations(getters.authUser, data);
      }
      else {
        res = await AuthService.updateGlobalConfiguration(getters.authUser, data);
      }

      if(res.status == 200) {
        commit('updateGlobalConfig', {data: data.config})
      }
      return res;
    }
    catch(e) {
      return {status: 500};
    }
  },

  async fetchCompanyGlobalConfigurations({getters, commit}) {
    try {
      let res = await AuthService.fetchCompanyGlobalConfigurations(getters.authUser);
      if(res && res.status == 200 && res.data) {
        commit('updateGlobalConfig', {data: res.data.config})
        commit('mergeAppConfigs', {data: res.data.config})
        return res.data.config;
      }
    }
    catch(e) {
      console.error("GlobalConfiguration not found", e)
    }
    return false;
  },

  async fetchCompanyConfiguration({state, getters, commit}, data) {
    try {
      if(!data) data = getters.authUser;

      let res = null;
      if(WyConstants.USE_OC_CONFIGURATION_API) {
        res = await AuthService.fetchCompanyConfigurations(data);
      }
      else {
        res = await AuthService.fetchCompanyConfiguration(data);
      }

      if(res && res.status == 200) {
        // eslint-disable-next-line no-unused-vars
        let mergedConfig = deepmerge(state.globalConfig, (res.data.config || {}), { arrayMerge: (destinationArray, sourceArray, options) => sourceArray });
        commit('updateCompanyConfig', {data: mergedConfig});
        commit('mergeAppConfigs', {data: mergedConfig});
        commit('updateSelectedCompany', {token: data.accessToken, companyId: data.companyId});
        state.authUser.isDevelopment = mergedConfig.isDevelopment;
        state.authUser.amplitudeDebug = mergedConfig.amplitudeDebug;
        return res.data.config;
      }
    }
    catch(e) {
      console.error("CompanyConfiguration not found", e)
    }
    return false;
  },

  async updateCompanyConfiguration({state, getters, commit}, data) {
    try {
      let res = null;
      if(WyConstants.USE_OC_CONFIGURATION_API) {
        res = await AuthService.updateCompanyConfigurations(getters.authUser, data);
      }
      else {
        res = await AuthService.updateCompanyConfiguration(getters.authUser, data);
      }

      if(res.status == 200) {
        // eslint-disable-next-line no-unused-vars
        let mergedConfig = deepmerge(state.globalConfig, data.config, { arrayMerge: (destinationArray, sourceArray, options) => sourceArray });
        commit('updateCompanyConfig', {data: mergedConfig});
        commit('mergeAppConfigs', {data: mergedConfig})
      }
      return res;
    }
    catch(e) {
      return {status: 500};
    }
  },

  async fetchAllConfigurations({dispatch}) {
    await dispatch('fetchCompanyGlobalConfigurations');
    await dispatch('fetchCompanyConfiguration');
  },

  async fetchCompanyProfileSettings({ getters, state, commit}) {
    try {
      let res = await AuthService.fetchCompanyProfileSettings(getters.authUser);
      if(res && res.status == 200) {
        if(res.data && res.data.targetSettings) {
          commit('updateCompanyProfileSettings', res.data)
        }
      }
    }
    catch(e) {
      console.error("CompanyProfileSettings not found", e, state.authUser)
    }
    return {};
  },

  async updateCompanyProfileSettings({getters, dispatch}, data) {    
    try {
      let res = await AuthService.updateCompanyProfileSettings(getters.authUser, data);
      if(res.status == 200) {
        await dispatch('fetchCompanyProfileSettings');
      }
      return res.status
    }
    catch(e) {
      return {status: 500}
    }
  },

  async fetchCompanyConfigSettings({ getters, state}, data) {
    try {
      let res = await AuthService.fetchCompanyConfigSettings(getters.authUser, data);
      if(res && res.status == 200) {
        return res.data
      }
    }
    catch(e) {
      console.error("CompanyConfigSettings not found", e, state.authUser)
    }
  },

  async updateCompanyConfigSettings({getters}, data) {    
    try {
      let res = await AuthService.updateCompanyConfigSettings(getters.authUser, data);
      return res.status
    }
    catch(e) {
      return {status: 500}
    }
  },

  async fetchCompanyAlertSettings({state}) {
    try {
      let res = await AuthService.fetchCompanyAlertSettings(state.authUser);
      if(res && res.status == 200) {
        if(res.data && res.data.alertSettings) {
          return res.data;
        }
      }
    }
    catch(e) {
      console.error("CompanyAlertSettings not found", e, state.authUser)
    }
    return {};
  },

  async updateCompanyAlertSettings({getters}, data) {    
    try {
      let res = await AuthService.updateCompanyAlertSettings(getters.authUser, data);
      if(res && res.status) {
        return res.status
      }
    }
    catch(e) {
      return {status: 500}
    }
  },

  async fetchCompanyPromptSettings({ state, commit}, authUser) {
    try {
      let res = await AuthService.fetchCompanyPromptSettings(authUser);
      if(res && res.status == 200) {
        if(res.data && res.data.prompts) {
          commit('updateCompanyPromptSettings', res.data)
        }
      }
    }
    catch(e) {
      console.error("CompanyProfileSettings not found", e, state.authUser)
    }
    return {};
  },

  async updateCompanyPromptSettings({getters, dispatch}, data) {    
    try {
      let res = await AuthService.updateCompanyPromptSettings(getters.authUser, data);
      if(res.status == 200) {
        await dispatch('fetchCompanyPromptSettings', getters.authUser);
      }
      return res.status;
    }
    catch(error) {
      throw new Error(error);
    }
  },

  resetAuth(context) {
    context.commit('resetLogin');
  },

  async logout({commit, getters}, reason) {
    // amplitude track logout
    AmplitureService.eventLog('logged_out', getters.authUser, {logout_method: reason});
    // reset default path
    commit('updateDefaultRoutePath', null);
    // reset user data
    commit('resetLogin');
  },

  async updateBrowserSessionStorageByUser({ state }, {key, value}) {
    if(state.authUser && state.authUser.user) {
      WyUtils.updateBrowserSessionStorageByUser(state.authUser, key, value);
    }
  },

  async resetBrowserSessionStorageByUser({ state }) {
    if(state.authUser && state.authUser.user) {
      WyUtils.resetBrowserSessionStorageByUser(state.authUser);
    }
  },

  async updateBrowserLocalStorageByUser({ state }, {key, value}) {
    if(state.authUser && state.authUser.user) {
      WyUtils.updateBrowserLocalStorageByUser(state.authUser, key, value);
    }
  },

  async resetBrowserLocalStorageByUser({ state }) {
    if(state.authUser && state.authUser.user) {
      WyUtils.resetBrowserLocalStorageByUser(state.authUser);
    }
  },

  async c1Auth({dispatch, commit}, {authHost, authOptions, sessionId, authUrl, productName, isIFramePage}) {
    return new Promise(async (resolve) => {        
      let res = await AuthService.authC1Token(authHost, authOptions, sessionId, productName);
      if(res && res.status == 200) {
        const authObj = res.data;
        if(authObj && authObj.user && authObj.token) {
          commit('updateAuthUser', {
            token: authObj.token, 
            accessToken: authObj.token, 
            authHost: authHost,
            sessionId: sessionId,
            productName: productName,
            authOptions: authOptions,
            user: authObj.user, 
            companyId: authObj.user.companyId, 
            company: authObj.user.company, 
            authUrl: authUrl});
          commit('updateTokenInfo', authObj.token);
          
          if(!isIFramePage) {
            await dispatch('fetchAllConfigurations');
          }
          //commit('updateSelectedCompany', {token: authObj.token, companyId: authObj.user.defaultCompanyId});
          sessionStorage.setItem("c1-sessionId", sessionId);
        }
        else {
          await dispatch('logoutAndReload');
        }
      }
      else if(res && res.status == 401 && res.data && res.data.errorCode == 5002) {
        await dispatch('logoutAndReload');
      }
      resolve(res);
    });
  },

  async updateAuthUserToken({commit}, token) {
    commit('updateAuthUserToken', token);
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}